import { AuditLog } from '@buyerflo/core/schemas/auditLog'
import { FormSection } from '@buyerflo/ui/components/FormSection'
import { useQuery } from '@tanstack/react-query'
import Loading from 'components/Loading'
import { format, isToday, isYesterday } from 'date-fns'
import { useMemo } from 'react'
import { useParams } from 'react-router'
import { Separator, SizableText, Text, View, XStack, YStack } from 'tamagui'
import { useTRPC } from 'utils/trpc'
import { CLIENT_DETAIL_MAX_WIDTH } from './helpers'

/** "Today · 09:42", "Yesterday · 16:30", or "30/04/26 · 09:00". */
const formatAuditTimestamp = (timestamp: number) => {
    // Guard against legacy/malformed entries missing a timestamp — date-fns would
    // otherwise render "Invalid Date" and a bad value could break the whole panel.
    if (!Number.isFinite(timestamp)) return 'Unknown time'
    const datePart = isToday(timestamp)
        ? 'Today'
        : isYesterday(timestamp)
          ? 'Yesterday'
          : format(timestamp, 'dd/MM/yy')
    return `${datePart} · ${format(timestamp, 'HH:mm')}`
}

const AuditLogDesktop = ({ auditLog }: { auditLog: AuditLog[] }) => (
    <FormSection>
        {auditLog.length === 0 ? (
            <SizableText
                size="$2"
                color="$colorMuted"
                alignSelf="center"
                paddingVertical="$4"
            >
                No activity yet.
            </SizableText>
        ) : (
            <YStack>
                {auditLog.map((event, i) => (
                    <XStack
                        key={`${event.timestamp}-${i}`}
                        gap="$4"
                        paddingVertical="$3"
                        alignItems="flex-start"
                        borderBottomWidth={i < auditLog.length - 1 ? 1 : 0}
                        borderBottomColor="$borderColor"
                    >
                        <SizableText
                            width={150}
                            flexShrink={0}
                            color="$colorMuted"
                            fontFamily="$mono"
                            size="$1"
                            style={{ whiteSpace: 'nowrap' }}
                        >
                            {formatAuditTimestamp(event.timestamp)}
                        </SizableText>
                        <YStack
                            flex={1}
                            gap="$1"
                        >
                            <Text
                                style={{ whiteSpace: 'pre-wrap' }}
                                fontSize="$2"
                                color="$color"
                            >
                                {event.details}
                            </Text>
                            <SizableText
                                fontSize={11}
                                color="$colorMuted"
                            >
                                {event.userName}
                            </SizableText>
                        </YStack>
                    </XStack>
                ))}
            </YStack>
        )}
    </FormSection>
)

const AuditLogMobile = ({ auditLog }: { auditLog: AuditLog[] }) =>
    auditLog.map((event, i) => (
        <View key={`${event.timestamp}-${i}`}>
            {i > 0 && <Separator marginVertical="$2" />}
            <Text>
                <Text fontWeight="bold">{event.userName}</Text> ({formatAuditTimestamp(event.timestamp)})
            </Text>
            <Text style={{ whiteSpace: 'pre-wrap' }}>{event.details}</Text>
        </View>
    ))

export default () => {
    const { purchaserId } = useParams<{ purchaserId: string }>()
    const trpc = useTRPC()

    const auditLogQuery = useQuery({
        ...trpc.purchaser.getAuditLog.queryOptions({ purchaserId: purchaserId! }),
        enabled: !!purchaserId,
    })

    // Latest activity first. Memoized so the whole list isn't re-sorted every render.
    const auditLog = useMemo(
        () => [...(auditLogQuery.data ?? [])].sort((a, b) => b.timestamp - a.timestamp),
        [auditLogQuery.data],
    )

    // Distinguish a failed/permission-denied fetch from a genuinely empty log,
    // rather than spinning on <Loading /> forever.
    if (auditLogQuery.isError) {
        return (
            <SizableText
                size="$2"
                color="$colorMuted"
                alignSelf="center"
                paddingVertical="$4"
            >
                Couldn't load activity. Please try again.
            </SizableText>
        )
    }

    if (!auditLogQuery.data) {
        return <Loading />
    }

    return (
        <>
            <View
                display="block"
                $md={{ display: 'none' }}
            >
                <AuditLogMobile auditLog={auditLog} />
            </View>
            <View
                display="none"
                maxWidth={CLIENT_DETAIL_MAX_WIDTH}
                $md={{ display: 'block' }}
            >
                <AuditLogDesktop auditLog={auditLog} />
            </View>
        </>
    )
}
